import React, { useMemo, useState } from "react";
import { FiSettings, FiTrendingUp, FiPhone, FiUserPlus, FiUserCheck, FiUsers } from "react-icons/fi";
import {
    FaRegComment,
    FaRegBookmark,
    FaInfoCircle,
    FaInstagram,
    FaTiktok,
    FaUser,
    FaFacebook,
    FaYoutube,
} from "react-icons/fa";
import { SiX } from "react-icons/si";
import { HiOutlineGlobeAlt, HiOutlineEye } from "react-icons/hi";
import { HiOutlineBuildingOffice2, HiOutlineSquares2X2 } from "react-icons/hi2";
import clsx from "clsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import { UserType } from "@/types";
import {
    getAgentProfileHeader,
    getAgentProfileStats,
    type AgentProfileHeader,
} from "@/services/AgentService";
import {
    findUserNotificationSetting,
    updateUserProfile,
    uploadUserProfileImage,
    type UpdateUserProfilePayload,
} from "@/services/UserService";
import type { ProfileTabId } from "./components/profile.types";
import { AgentEditProfileModal } from "./components/EditProfileModal";
import { MyListingsTab, CommentsTab, SavedTab, AboutTab } from "./components/ProfileTabsContent";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import GuestAuthPromptModal from "@/components/common/GuestAuthPromptModal";
import useAgentFollow from "@/hooks/useAgentFollow";

type TabMeta = {
    id: ProfileTabId;
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
};

const ACTIVITY_TABS: TabMeta[] = [
    { id: "listings", label: "My Listings", Icon: FiTrendingUp },
    { id: "comments", label: "Comments", Icon: FaRegComment },
    { id: "saved", label: "Saved", Icon: FaRegBookmark },
];

const ABOUT_TAB: TabMeta = { id: "about", label: "About", Icon: FaInfoCircle };

function toExternalUrl(url?: string | null): string | undefined {
    const value = (url ?? "").trim();
    if (!value) return undefined;
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
}

interface AgentProfileProps {
    nickname: string;
    onBack: () => void;
}

export const AgentProfile: React.FC<AgentProfileProps> = ({ nickname, onBack }) => {
    const { isAuthenticated } = useAuth0();
    const { userState, fetchUserData, setUserState } = useUser();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const currentNickname = userState?.nickname ?? undefined;
    const requestingUserId = userState?.id ? String(userState.id) : undefined;

    const profileNickname = (nickname ?? "").trim();
    const isOwnProfile = !!currentNickname && profileNickname.toLowerCase() === currentNickname.toLowerCase();
    const isAgentUser = userState?.type === UserType.AGENT;

    const [activeTab, setActiveTab] = useState<ProfileTabId>("listings");
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState(false);
    const { handleGoogle, handleEmail } = useOneTapAuth({
        redirectPath: `/profile`,
        version: "puzzle",
        onClose: () => setShowGuestAuthPrompt(false),
        location: "Google 1-Tap Modal",
    });

    const {
        data: agentHeader,
        isLoading: isHeaderLoading,
        isError: isHeaderError,
    } = useQuery({
        queryKey: ["agent-profile-header", profileNickname, requestingUserId ?? "anonymous"],
        queryFn: () => getAgentProfileHeader(profileNickname, { requestingUserId }),
        enabled: !!profileNickname,
        retry: false,
    });

    const {
        data: agentStats,
        isLoading: isStatsLoading,
        isError: isStatsError,
    } = useQuery({
        queryKey: ["agent-profile-stats", profileNickname],
        queryFn: () => getAgentProfileStats(profileNickname),
        enabled: !!profileNickname && !!agentHeader,
        retry: false,
    });

    const { data: profileNotificationSettings } = useQuery({
        queryKey: ["agent-profile-notification-settings", userState?.id],
        queryFn: () => findUserNotificationSetting(String(userState?.id)),
        enabled: !!isOwnProfile && !!userState?.id,
        retry: false,
    });

    const followerUserId = userState?.id ? String(userState.id) : null;
    const followedUserId = agentHeader?.userId ? String(agentHeader.userId) : null;
    const { isFollowing, followerCount, isPending: isFollowPending, toggleFollow } = useAgentFollow({
        followerUserId,
        followedUserId,
        initialIsFollowing: agentHeader?.isFollowing,
        initialFollowerCount: agentHeader?.followerCount,
        isAuthenticated,
        onRequireAuth: () => setShowGuestAuthPrompt(true),
        onMissingIds: () => toast.error("Missing agent user id from profile API."),
        mixpanelLocation: "Profile",
        nicknameFollowed: agentHeader?.nickname ?? profileNickname ?? null,
    });

    const agentProfile = useMemo(() => {
        const fallbackNickname = profileNickname || currentNickname || "";
        const baseHandle = fallbackNickname ? `@${fallbackNickname}` : "";

        const header = agentHeader;
        const stats = agentStats;

        const nameFromHeader =
            header?.displayName ||
            (header?.nickname ? String(header.nickname) : null) ||
            (userState?.first_name || userState?.last_name
                ? `${userState.first_name ?? ""} ${userState.last_name ?? ""}`.trim()
                : userState?.nickname || fallbackNickname || "Player");

        // Only fall back to the signed-in user's own bio on their own profile;
        // on someone else's profile an empty bio must render empty, never the viewer's text.
        const bio =
            (header?.bio as string | null | undefined) ??
            (isOwnProfile ? (userState?.bio as string | null | undefined) : null) ??
            "";

        const websiteUrl = toExternalUrl((header?.primaryLink as string | null | undefined) ?? undefined);
        const socialProfiles: AgentProfileHeader["socialProfiles"] | undefined = header?.socialProfiles;

        const listingsCount = stats?.playablePropertiesCount ?? 0;
        const playsCountRaw = stats?.userGuessCount ?? 0;
        const playsCount = playsCountRaw ? playsCountRaw.toLocaleString() : "0";

        // Prefer explicit true; treat undefined as private for non-owners to avoid flashing non-private UI.
        const profilePrivateRaw = header?.profilePrivate ?? stats?.profilePrivate;
        const profilePrivate = profilePrivateRaw === true;

        const inferredIsAgent = Boolean(header) || (isOwnProfile && isAgentUser);

        return {
            nickname: fallbackNickname,
            name: nameFromHeader,
            handle: header?.nickname ? `@${header.nickname}` : baseHandle,
            company: header?.brokerage ?? undefined,
            bio,
            phone: (header?.phoneNumber as string | null | undefined) ?? undefined,
            websiteUrl,
            websiteLabel: websiteUrl || undefined,
            avatarUrl: (header?.profilePhotoUrl as string | null | undefined) ?? undefined,
            isAgent: inferredIsAgent,
            isPrivate: profilePrivate,
            stats: {
                listingsCount,
                playsCount,
            },
            socialLinks: {
                instagram: toExternalUrl(socialProfiles?.instagram ?? undefined),
                tiktok: toExternalUrl(socialProfiles?.tiktok ?? undefined),
                twitter: toExternalUrl(socialProfiles?.xTwitter ?? undefined),
                facebook: toExternalUrl(socialProfiles?.facebook ?? undefined),
                youtube: toExternalUrl(socialProfiles?.youtube ?? undefined),
            },
        };
    }, [agentHeader, agentStats, currentNickname, profileNickname, isAgentUser, isOwnProfile, userState]);

    const isLoadingProfile = (isHeaderLoading || isStatsLoading) && !agentHeader && !agentStats;
    const isProfileError = (isHeaderError || isStatsError) && !agentHeader && !agentStats;

    const isPrivateOther = !isOwnProfile && agentProfile.isPrivate;

    const trackAgentHeaderClick = (clickType: string) => {
        sendMixpanelEvent("Agent Click", {
            isAuthenticated,
            pageUrl: window.location.href,
            user_id: userState?.id ?? null,
            nickname: userState?.nickname ?? null,
            email: userState?.emailAddress ?? null,
            agentName: agentProfile.name,
            agentMlsId: undefined,
            clickType,
            clickLocation: "agent_profile_header",
        });
    };

    const handleContactClick = async () => {
        if (!agentProfile.phone) return;
        const telNumber = agentProfile.phone.replace(/[^\d+]/g, "");
        const nextOpen = !isContactOpen;
        setIsContactOpen(nextOpen);
        if (!nextOpen) return;

        trackAgentHeaderClick("phone");

        try {
            window.location.href = `tel:${telNumber}`;
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(agentProfile.phone);
            }
        } catch {
            // ignore
        }
    };

    const handleFollowToggle = () => {
        toggleFollow();
    };

    const handleSaveProfile = async (payload: UpdateUserProfilePayload, profileImageFile?: File | null) => {
        if (!userState?.id) {
            throw new Error("Missing user id");
        }
        const updatedUser = await updateUserProfile(userState.id, payload);
        const updatedNickname = updatedUser?.nickname?.trim();
        const nextShowScoreLeaderboard =
            typeof payload.showScoreLeaderboard === "boolean"
                ? payload.showScoreLeaderboard
                : updatedUser?.showScoreLeaderboard;
        const nextProfileNickname = updatedNickname || profileNickname;

        const nextUserProps: Partial<typeof userState> = {};
        if (updatedUser?.nickname && updatedUser.nickname !== userState.nickname) {
            nextUserProps.nickname = updatedUser.nickname;
        }
        if (
            typeof nextShowScoreLeaderboard === "boolean" &&
            nextShowScoreLeaderboard !== userState.showScoreLeaderboard
        ) {
            nextUserProps.showScoreLeaderboard = nextShowScoreLeaderboard;
        }

        if (Object.keys(nextUserProps).length > 0) {
            setUserState(userState, userState.is_created, nextUserProps);
        }
        queryClient.setQueryData(["agent-profile-header", nextProfileNickname], (prev: any) =>
            prev
                ? {
                      ...prev,
                      profilePrivate: updatedUser?.profilePrivate ?? prev.profilePrivate,
                      displayName: updatedUser?.displayName ?? prev.displayName,
                      nickname: updatedUser?.nickname ?? prev.nickname,
                      bio: updatedUser?.bio ?? prev.bio,
                      profilePhotoUrl: updatedUser?.profilePhotoUrl ?? prev.profilePhotoUrl,
                      showScoreLeaderboard: nextShowScoreLeaderboard ?? prev.showScoreLeaderboard,
                  }
                : prev,
        );
        if (profileImageFile) {
            await uploadUserProfileImage(userState.id, profileImageFile);
            await fetchUserData(userState.id);
        }
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["agent-profile-header", nextProfileNickname] }),
            queryClient.invalidateQueries({ queryKey: ["agent-profile-stats", nextProfileNickname] }),
            queryClient.invalidateQueries({ queryKey: ["agent-profile-properties", nextProfileNickname] }),
            queryClient.invalidateQueries({
                queryKey: ["agent-profile-notification-settings", userState?.id],
            }),
        ]);

        if (updatedNickname && updatedNickname.toLowerCase() !== profileNickname.toLowerCase()) {
            navigate(`/profile/${encodeURIComponent(updatedNickname)}`, { replace: true });
        }
    };

    if (isLoadingProfile) {
        return (
            <div className="tw-max-w-[600px] tw-mx-auto tw-min-h-[calc(100vh-9svh)] tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-[var(--dark-grey)]">
                Loading profile…
            </div>
        );
    }

    if (isProfileError) {
        return (
            <div className="tw-max-w-[600px] tw-mx-auto tw-min-h-[calc(100vh-9svh)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-px-6 tw-gap-3">
                <div>
                    <p className="tw-font-semibold tw-mb-1">Agent profile not found</p>
                    <p className="tw-m-0 tw-text-[13px] md:tw-text-sm tw-text-[var(--nobel-gray)]">
                        We couldn&apos;t load this agent&apos;s profile. It may be private, deleted, or the link might
                        be incorrect.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onBack}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-[var(--orange)] tw-bg-white tw-text-[var(--orange)] tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-transition-colors hover:tw-bg-[var(--orange)] hover:tw-text-white"
                >
                    Go back
                </button>
            </div>
        );
    }

    return (
        <>
            <div
                className="tw-max-w-[600px] md:tw-max-w-[1200px] tw-mx-auto tw-text-white tw-px-6 tw-pt-4 md:tw-pt-6 tw-pb-2 tw-flex tw-flex-col tw-items-center tw-text-center tw-bg-[linear-gradient(175deg,_rgb(61,24,115)_0%,_rgb(42,16,82)_40%,_rgb(26,10,53)_100%)]"
            >
                <div className="tw-w-full tw-max-w-[600px] md:tw-max-w-[1200px] tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-justify-between tw-gap-4">
                        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-gap-3 tw-w-full md:tw-w-auto">
                            <div className="tw-relative tw-m-2">
                                {agentProfile.avatarUrl ? (
                                    <img
                                        src={agentProfile.avatarUrl}
                                        alt={agentProfile.name}
                                        className="tw-w-[82px] tw-h-[82px] tw-rounded-full tw-object-cover tw-border-2 tw-border-[var(--orange)]"
                                    />
                                ) : (
                                    <div className="tw-w-[82px] tw-h-[82px] tw-rounded-full tw-border-2 tw-border-[var(--orange)] tw-bg-white/10 tw-flex tw-items-center tw-justify-center">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-white/90">
                                            <FaUser className="tw-text-[26px]" />
                                        </div>
                                    </div>
                                )}
                                {agentProfile.isAgent && (
                                    <span className="tw-absolute tw-bottom-[-6px] tw-left-[70px] tw--translate-x-1/2 tw-bg-[var(--orange)] tw-text-white tw-text-[10px] tw-font-bold tw-px-[10px] tw-py-[2px] tw-rounded tw-uppercase tw-tracking-[0.06em]">
                                        Agent
                                    </span>
                                )}
                            </div>
                            <div className="tw-text-center md:tw-text-left">
                                <h1 className="tw-font-['Merriweather',serif] tw-font-bold tw-text-xl tw-m-0 tw-p-0 tw-mb-[2px]">
                                    {agentProfile.name}
                                </h1>
                                <p className="tw-text-sm md:tw-text-base tw-text-white/50 tw-mb-[1px]">
                                    {agentProfile.handle}
                                </p>
                                {agentProfile.company && (
                                    <div className="tw-flex tw-items-center tw-justify-center md:tw-justify-start tw-gap-1 tw-text-[13px] md:tw-text-sm tw-mt-[2px] tw-opacity-95">
                                        <HiOutlineBuildingOffice2 className="tw-text-[15px] tw-text-[var(--orange)]" />
                                        <span>{agentProfile.company}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="tw-w-full md:tw-w-auto tw-flex tw-flex-col tw-items-center md:tw-items-end tw-justify-center md:tw-justify-end">
                            {isOwnProfile && (
                                <button
                                    type="button"
                                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg !tw-border !tw-border-solid !tw-border-white/30 tw-bg-white/10 tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-transition-colors hover:tw-bg-white/20"
                                    onClick={() => setIsEditingProfile(true)}
                                >
                                    <FiSettings className="tw-text-[15px]" />
                                    Edit Profile
                                </button>
                            )}
                            {!isOwnProfile && (
                                <button
                                    type="button"
                                    className={clsx(
                                        "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg !tw-border !tw-border-solid tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-transition-colors",
                                        isFollowing
                                            ? "!tw-border-white/30 tw-bg-white/10 tw-text-white hover:tw-bg-white/20"
                                            : "!tw-border-[var(--orange)] tw-bg-[var(--orange)] tw-text-white hover:tw-bg-[#f5a037]",
                                    )}
                                    disabled={isFollowPending}
                                    onClick={handleFollowToggle}
                                >
                                    {isFollowPending
                                        ? "..."
                                        : isFollowing ? (
                                              <>
                                                  <FiUserCheck className="tw-text-[14px]" />
                                                  Following
                                              </>
                                          ) : (
                                              <>
                                                  <FiUserPlus className="tw-text-[14px]" />
                                                  Follow
                                              </>
                                          )}
                                </button>
                            )}
                        </div>
                    </div>

                    <p className="tw-text-sm md:tw-text-[15px] tw-leading-relaxed tw-max-w-[26rem] tw-mb-0 tw-text-center md:tw-text-left">
                        {agentProfile.bio}
                    </p>

                    {agentProfile.websiteUrl && (
                        <a
                            href={agentProfile.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackAgentHeaderClick("website")}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs md:tw-text-sm tw-mb-2 tw-no-underline tw-self-center md:tw-self-start tw-group"
                        >
                            <HiOutlineGlobeAlt className="tw-text-[15px] tw-text-[var(--orange)]" />
                            <span className="tw-text-white/50 tw-transition-colors tw-duration-150 group-hover:tw-text-[var(--orange)]">
                                {agentProfile.websiteLabel ?? "My Website"}
                            </span>
                        </a>
                    )}
                </div>
                <hr className="tw-w-full tw-border-t tw-border-white/20 tw-mt-2 tw-mb-4" />
                {/* Desktop: stats on left, social+contact on right in a single row. Mobile: stacked. */}
                <div className="tw-flex tw-w-full tw-flex-col md:tw-flex-row tw-items-center md:tw-items-center tw-justify-center md:tw-justify-between tw-gap-3 md:tw-gap-6 tw-mb-3 md:tw-mb-4 tw-text-xs md:tw-text-sm">
                    {/* Followers / listings / plays */}
                    <div className="tw-flex tw-flex-wrap tw-justify-center md:tw-justify-start tw-gap-2">
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <FiUsers className="tw-text-[16px] tw-text-[var(--orange)]" />
                            <span className="tw-text-white/50">
                                <span className="tw-font-semibold tw-text-white/90">
                                    {followerCount.toLocaleString()}
                                </span>{" "}
                                {followerCount === 1 ? "follower" : "followers"}
                            </span>
                        </div>
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <HiOutlineSquares2X2 className="tw-text-[16px] tw-text-[var(--orange)]" />
                            <span className="tw-text-white/50">
                                <span className="tw-font-semibold tw-text-white/90">
                                    {agentProfile.stats.listingsCount}
                                </span>{" "}
                                listings
                            </span>
                        </div>
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-px-3 tw-py-1.5">
                            <HiOutlineEye className="tw-text-[16px] tw-text-[var(--orange)]" />
                            <span className="tw-text-white/50">
                                <span className="tw-font-semibold tw-text-white/90">
                                    {agentProfile.stats.playsCount}
                                </span>{" "}
                                plays
                            </span>
                        </div>
                    </div>

                    {/* Social + contact group */}
                    <div className="tw-flex tw-flex-wrap md:tw-flex-nowrap tw-items-center tw-gap-2 tw-justify-center md:tw-justify-end">
                        <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-1.5 tw-flex-wrap md:tw-flex-nowrap">
                            {agentProfile.socialLinks?.instagram && (
                                <a
                                    href={agentProfile.socialLinks.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Instagram"
                                    onClick={() => trackAgentHeaderClick("instagram")}
                                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-transition-colors hover:tw-bg-white/20 tw-group"
                                >
                                    <FaInstagram className="tw-text-[18px] tw-text-white/50 group-hover:tw-text-white" />
                                </a>
                            )}
                            {agentProfile.socialLinks?.tiktok && (
                                <a
                                    href={agentProfile.socialLinks.tiktok}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="TikTok"
                                    onClick={() => trackAgentHeaderClick("tiktok")}
                                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-transition-colors hover:tw-bg-white/20 tw-group"
                                >
                                    <FaTiktok className="tw-text-[18px] tw-text-white/50 group-hover:tw-text-white" />
                                </a>
                            )}
                            {agentProfile.socialLinks?.youtube && (
                                <a
                                    href={agentProfile.socialLinks.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="YouTube"
                                    onClick={() => trackAgentHeaderClick("youtube")}
                                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-transition-colors hover:tw-bg-white/20 tw-group"
                                >
                                    <FaYoutube className="tw-text-[18px] tw-text-white/50 group-hover:tw-text-white" />
                                </a>
                            )}
                            {agentProfile.socialLinks?.facebook && (
                                <a
                                    href={agentProfile.socialLinks.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Facebook"
                                    onClick={() => trackAgentHeaderClick("facebook")}
                                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-transition-colors hover:tw-bg-white/20 tw-group"
                                >
                                    <FaFacebook className="tw-text-[18px] tw-text-white/50 group-hover:tw-text-white" />
                                </a>
                            )}
                            {agentProfile.socialLinks?.twitter && (
                                <a
                                    href={agentProfile.socialLinks.twitter}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="X / Twitter"
                                    onClick={() => trackAgentHeaderClick("x")}
                                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-lg tw-border tw-border-white/30 tw-bg-white/10 tw-transition-colors hover:tw-bg-white/20 tw-group"
                                >
                                    <SiX className="tw-text-[16px] tw-text-white/50 group-hover:tw-text-white" />
                                </a>
                            )}
                        </div>

                        {agentProfile.phone && (
                            <div className="tw-relative">
                                <button
                                    type="button"
                                    aria-label="Contact phone number"
                                    className={clsx(
                                        "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg !tw-border !tw-border-solid tw-text-xs md:tw-text-sm tw-px-4 tw-py-1.5 tw-font-semibold tw-no-underline tw-transition-colors",
                                        isContactOpen
                                            ? "!tw-border-[var(--orange)] tw-bg-[var(--orange)] tw-text-white !tw-px-1"
                                            : "!tw-border-[var(--orange)] tw-bg-white/5 tw-text-[var(--orange)] hover:tw-bg-[var(--orange)]/10",
                                    )}
                                    onClick={handleContactClick}
                                >
                                    {!isContactOpen && (
                                        <>
                                            <FiPhone className="tw-text-[15px] tw-text-[var(--orange)]" />
                                            <span className="tw-text-[var(--orange)]">Contact</span>
                                        </>
                                    )}
                                    {isContactOpen && (
                                        <span className="tw-text-white tw-select-text">{agentProfile.phone}</span>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sticky tabs - hidden for other user's private profile */}
            {isOwnProfile || !agentProfile.isPrivate ? (
                <div className="tw-sticky tw-top-[9svh] tw-z-40 tw-bg-white tw-border-b tw-border-[var(--grey)]">
                    <div className="tw-flex tw-max-w-[600px] md:tw-max-w-[1200px] tw-mx-auto">
                        {[...ACTIVITY_TABS, ABOUT_TAB].map((tab) => {
                            const isActive = activeTab === tab.id;
                            const isDisabled = tab.id !== "listings";
                            const baseClasses =
                                "tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-py-3 tw-text-[11px] md:tw-text-xs tw-transition-colors tw-bg-white";
                            const activeClasses = "tw-text-[var(--orange)] tw-font-semibold";
                            const inactiveClasses = "tw-text-[var(--nobel-gray)]";
                            const Icon = tab.Icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={clsx(
                                        baseClasses,
                                        isActive ? activeClasses : inactiveClasses,
                                        isDisabled && "tw-opacity-50 tw-cursor-not-allowed",
                                    )}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        setActiveTab(tab.id);
                                    }}
                                >
                                    <Icon className="tw-text-[16px]" />
                                    <span>{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Active tab underline indicator */}
                    {(() => {
                        const tabs = [...ACTIVITY_TABS, ABOUT_TAB];
                        const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);
                        if (activeIndex === -1) return null;
                        const widthPercent = 100 / tabs.length;
                        return (
                            <div
                                className="tw-absolute tw-bottom-0 tw-h-[2px] tw-bg-[var(--orange)] tw-transition-all tw-duration-200"
                                style={{
                                    width: `${widthPercent}%`,
                                    left: `${widthPercent * activeIndex}%`,
                                }}
                            />
                        );
                    })()}
                </div>
            ) : null}

            {/* Content */}
            <div className="tw-bg-white tw-rounded-b-3xl tw-shadow-md tw-px-4 sm:tw-px-6 tw-py-5 tw-min-h-[200px]">
                {isPrivateOther ? (
                    <p className="tw-text-center tw-text-sm md:tw-text-base tw-text-[var(--dark-grey)] tw-py-8">
                        This user&apos;s profile is private.
                    </p>
                ) : (
                    <>
                        {activeTab === "listings" && <MyListingsTab nickname={profileNickname} />}
                        {activeTab === "comments" && <CommentsTab />}
                        {activeTab === "saved" && <SavedTab />}
                        {activeTab === "about" && <AboutTab />}
                    </>
                )}
            </div>

            <AgentEditProfileModal
                open={isEditingProfile}
                onClose={() => setIsEditingProfile(false)}
                onSave={handleSaveProfile}
                profile={{
                    ...agentProfile,
                    emailAddress: userState?.emailAddress ?? undefined,
                    handle: agentProfile.handle,
                    showScoreLeaderboard: userState?.showScoreLeaderboard ?? agentHeader?.showScoreLeaderboard,
                    profilePrivate: agentHeader?.profilePrivate,
                    notificationSettings: profileNotificationSettings,
                }}
            />
            <GuestAuthPromptModal
                show={showGuestAuthPrompt}
                onClose={() => setShowGuestAuthPrompt(false)}
                onGoogle={handleGoogle}
                onEmail={handleEmail}
                description="Sign in to follow this agent and get updates"
                source="follow"
            />
        </>
    );
};

